import { DateTime } from 'luxon';
import { reduceNumber, digitSum, KARMIC_DEBT_MAP } from './reductionRules';
import { letterValue, stripToLetters, sumVowelsConsonants, buildNameLetterGrid } from './letterValues';

function mergeKarmicHits(...lists) {
  const out = [];
  lists.forEach((list) => {
    if (Array.isArray(list)) out.push(...list);
  });
  return [...new Set(out)];
}

function reduceMonthComponent(month) {
  const m = Math.min(12, Math.max(1, month));
  if (m === 11) return { value: 11, raw: m, karmicHits: [] };
  const r = reduceNumber(m);
  return { value: r.value, raw: m, karmicHits: r.karmicHits };
}

function reduceDayComponent(day) {
  const d = Math.min(31, Math.max(1, day));
  if (d === 11 || d === 22) return { value: d, raw: d, karmicHits: [] };
  const r = reduceNumber(d);
  return { value: r.value, raw: d, karmicHits: r.karmicHits };
}

function reduceYearComponent(year) {
  const y = Math.floor(year);
  const first = digitSum(y);
  const r = reduceNumber(first);
  const karmic = [];
  if (KARMIC_DEBT_MAP[first]) karmic.push(KARMIC_DEBT_MAP[first]);
  return { value: r.value, raw: y, digitSum: first, karmicHits: mergeKarmic(karmic, r.karmicHits) };
}

function mergeKarmic(a, b) {
  return [...new Set([...(a || []), ...(b || [])])];
}

function expressionFromName(fullName) {
  const letters = stripToLetters(fullName);
  const raw = letters.split('').reduce((s, ch) => s + letterValue(ch), 0);
  const r = reduceNumber(raw);
  return {
    raw,
    value: r.value,
    isMaster: [11, 22, 33].includes(r.value),
    karmicHits: r.karmicHits,
  };
}

/**
 * Parse birth date from raw input — no locale-based parsing.
 * Accepts: YYYY-MM-DD (native date input), YYYY-M-D, DD.MM.YYYY, MM/DD/YYYY.
 * Returns Luxon DateTime (date-only, local zone) or null.
 */
export function parseBirthDate(rawInput) {
  const s = String(rawInput ?? '').trim();
  if (!s) return null;

  // Native <input type="date"> value and strict ISO YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const dt = DateTime.fromISO(s, { zone: 'local' });
    if (dt.isValid) return dt;
  }

  // ISO-like YYYY-M-D
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) {
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const d = parseInt(m[3], 10);
    const dt = DateTime.fromObject({ year: y, month: mo, day: d }, { zone: 'local' });
    if (dt.isValid) return dt;
  }

  // DD.MM.YYYY (European dotted)
  m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s);
  if (m) {
    const d = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const y = parseInt(m[3], 10);
    const dt = DateTime.fromObject({ year: y, month: mo, day: d }, { zone: 'local' });
    if (dt.isValid) return dt;
  }

  // MM/DD/YYYY (slash-separated)
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) {
    const mo = parseInt(m[1], 10);
    const d = parseInt(m[2], 10);
    const y = parseInt(m[3], 10);
    const dt = DateTime.fromObject({ year: y, month: mo, day: d }, { zone: 'local' });
    if (dt.isValid) return dt;
  }

  return null;
}

/**
 * Normalized calendar parts + ISO date string for storage.
 */
export function normalizeBirthDateParts(rawInput) {
  const dt = parseBirthDate(rawInput);
  if (!dt) return null;
  return {
    year: dt.year,
    month: dt.month,
    day: dt.day,
    isoDate: dt.toISODate(),
  };
}

export function computeCoreNumbers(input) {
  const fullBirthName = String(input.fullBirthName || '').trim();
  const currentName = String(input.currentName || '').trim();
  const rawDob = String(input.dateOfBirth || '').trim();

  const dt = parseBirthDate(rawDob);
  if (!dt) {
    return {
      ok: false,
      error:
        'Please enter a valid date of birth. Use YYYY-MM-DD, or DD.MM.YYYY, or MM/DD/YYYY.',
    };
  }

  const month = dt.month;
  const day = dt.day;
  const year = dt.year;
  const birthDateIso = dt.toISODate();

  const rm = reduceMonthComponent(month);
  const rd = reduceDayComponent(day);
  const ry = reduceYearComponent(year);

  const lpSum = rm.value + rd.value + ry.value;
  const lpReduced = reduceNumber(lpSum);
  const lifePath = {
    value: lpReduced.value,
    isMaster: [11, 22, 33].includes(lpReduced.value),
    components: { month: rm.value, day: rd.value, year: ry.value },
    karmicHits: mergeKarmicHits(rm.karmicHits, rd.karmicHits, ry.karmicHits, lpReduced.karmicHits),
  };

  const expression = expressionFromName(fullBirthName);
  const { vowelSum, consonantSum } = sumVowelsConsonants(fullBirthName);
  const soulUrgeR = reduceNumber(vowelSum);
  const personalityR = reduceNumber(consonantSum);

  const soulUrge = {
    value: soulUrgeR.value,
    isMaster: [11, 22, 33].includes(soulUrgeR.value),
    raw: vowelSum,
    karmicHits: soulUrgeR.karmicHits,
  };

  const personality = {
    value: personalityR.value,
    isMaster: [11, 22, 33].includes(personalityR.value),
    raw: consonantSum,
    karmicHits: personalityR.karmicHits,
  };

  const birthdayR = reduceDayComponent(day);
  const birthday = {
    value: birthdayR.value,
    isMaster: [11, 22].includes(birthdayR.value),
    raw: day,
  };

  const matSum = lifePath.value + expression.value;
  const matR = reduceNumber(matSum);
  const maturity = {
    value: matR.value,
    isMaster: [11, 22, 33].includes(matR.value),
    karmicHits: matR.karmicHits,
  };

  const targetYear = input.targetYear != null ? Number(input.targetYear) : DateTime.now().year;
  const pyMonth = reduceMonthComponent(month).value;
  const pyDay = reduceDayComponent(day).value;
  const pyYearPart = reduceYearComponent(targetYear).value;
  const pySum = pyMonth + pyDay + pyYearPart;
  const pyR = reduceNumber(pySum);
  const personalYear = {
    value: pyR.value,
    isMaster: [11, 22, 33].includes(pyR.value),
    year: targetYear,
    karmicHits: pyR.karmicHits,
  };

  const calMonth = input.targetCalendarMonth != null
    ? Math.min(12, Math.max(1, Number(input.targetCalendarMonth)))
    : DateTime.now().month;
  const pmSum = personalYear.value + calMonth;
  const pmR = reduceNumber(pmSum);
  const personalMonth = {
    value: pmR.value,
    isMaster: [11, 22, 33].includes(pmR.value),
    month: calMonth,
    karmicHits: pmR.karmicHits,
  };

  const nameGrid = buildNameLetterGrid(fullBirthName);
  const missingNumbers = Object.keys(nameGrid.counts)
    .map(Number)
    .filter((d) => nameGrid.counts[d] === 0);

  const currentExpression = currentName
    ? expressionFromName(currentName)
    : null;

  const allKarmic = mergeKarmicHits(
    lifePath.karmicHits,
    expression.karmicHits,
    soulUrge.karmicHits,
    personality.karmicHits,
    maturity.karmicHits,
    personalYear.karmicHits,
    personalMonth.karmicHits,
    currentExpression?.karmicHits || [],
  );

  return {
    ok: true,
    meta: {
      birthDate: birthDateIso,
      birthDateInput: rawDob,
      month,
      day,
      year,
      fullBirthName,
      firstName: String(input.firstName || '').trim() || null,
      currentName: currentName || null,
      focusQuestion: String(input.focusQuestion || '').trim() || null,
      preferredLifeArea: String(input.preferredLifeArea || '').trim() || null,
      targetYear,
      targetCalendarMonth: calMonth,
      sampleMode: Boolean(input.sampleMode),
      samplePersonaId: input.samplePersonaId || null,
      profileKind: String(input.profileKind || 'numerology_only').trim() || 'numerology_only',
    },
    numbers: {
      lifePath,
      expression,
      soulUrge,
      personality,
      birthday,
      maturity,
      personalYear,
      personalMonth,
      currentExpression,
    },
    nameGrid,
    missingNumbers,
    karmicDebtMarkers: allKarmic,
  };
}
